/**
 * What the microphone's denoiser is doing, for the flow report.
 *
 * On 2026-10-27 two people were unmuted and sent the room only silence, and
 * the only way to see it was their mic arriving at the SFU at ~21 kbps, the
 * rate Opus produces for silence. That says THAT it was silent, not where:
 * no microphone, an OS mute, or the denoiser. These levels say where. The raw
 * mic loud and the output silent is the denoiser; both silent is the device.
 *
 * Kept apart from `noise-cancellation.ts` so `client-flow-report.ts` can read
 * it without importing DeepFilterNet. One writer at a time: the live graph.
 */
import type { SilencedReason } from './noise-cancellation'

export type DenoiserState = {
  /** A graph is live: the published microphone is denoised. */
  on: boolean
  /** Raw microphone and denoised output at the last check, dBFS (-100 = silence). */
  in?: number
  out?: number
  /** Muted: the model is idle and passes its input through. */
  idle?: boolean
  /** Why the last graph gave the raw microphone back. Kept until another graph goes live. */
  off?: SilencedReason
}

let state: DenoiserState | null = null
let owner: object | null = null

export function reportDenoiser(by: object, next: DenoiserState): void {
  owner = by
  state = next
}

/**
 * The graph is gone. Forget it, unless it went because it silenced someone:
 * that stays in the report until a new graph replaces it, so a trace taken
 * after the fact still sees that noise cancellation dropped out, and why.
 */
export function releaseDenoiser(by: object): void {
  if (owner !== by) return
  owner = null
  if (!state?.off) state = null
}

export function denoiserState(): DenoiserState | null {
  return state
}

/** dBFS for the report: whole numbers, with silence (and NaN) floored at -100. */
export function reportedLevel(db: number): number {
  return Number.isFinite(db) ? Math.max(-100, Math.round(db)) : -100
}
